export interface FormFieldConfig {
  label: string;
  key: string;
  type: FormFieldType;
  validations?: FormFieldValidations[];
  options?: DropdownOption[];
  defaultValue?: FormFieldValue;
  placeholder?: string;
  required?: boolean;
  displayConditions?: FormFieldConditionGroup;
  requiredConditions?: FormFieldConditionGroup;
  disabled?: boolean;
  isValidatedBy?: string;
  showPasswordStrength?: boolean;
  // honored only by the Password renderer — clears the prefilled value on first
  // focus so the user types a fresh secret, and restores it on blur (re-arming the
  // focus-clear) if the field is left empty, so the "value exists" hint returns.
  // Ignored by other field types.
  clearOnFocus?: boolean;
  newLine?: boolean;
  showLabel?: boolean;
  // DateRange only: one-click ranges ending today, e.g. { label: '24h', days: 1 }.
  dateRangePresets?: FormFieldDateRangePreset[];
}

export interface FormFieldDateRangePreset {
  label: string;
  days: number;
}

export type FormFieldMultiSelectValues = Record<string, boolean>;

// Both ends are 'yyyy-MM-dd'. `end` is empty while the user has picked only the
// range start, so consumers must treat a half-open range as "not set yet".
export interface FormFieldDateRangeValue {
  start: string;
  end: string;
}

export type FormFieldValue = string | number | boolean | FormFieldMultiSelectValues | FormFieldDateRangeValue;

export type FormFieldValues = Record<string, FormFieldValue>;

export enum FormFieldType {
  Text = 'text',
  Select = 'select',
  SelectWithCustomText = 'select-with-custom-text',
  MultiSelect = 'multi-select',
  RadioSelect = 'radio-select',
  MultiSelectDropdown = 'multi-select-dropdown',
  Checkbox = 'checkbox',
  Date = 'date',
  DateRange = 'date-range',
  Time = 'time',
  Password = 'password',
  Textarea = 'textarea',
  EmptySpace = 'empty',
}

export enum FormFieldVariant {
  Normal = 'normal',
  Borderless = 'borderless',
}

// Outcome of gating a facility option against the live-EHR site list:
// 'verified' — on the referral and confirmed available; 'notAvailable' — on the
// referral but absent from the EHR (option disabled); 'new' — in the EHR but
// not on the original referral.
export type FacilityVerification = 'verified' | 'notAvailable' | 'new';

export interface DropdownOption {
  id: string | number | null;
  label: string;
  explanation?: string;
  // When true the option renders greyed-out and cannot be selected (per-option,
  // distinct from the whole-field `disabled`). Used by SNF-378 to block recipient
  // facilities the EHR no longer lists.
  disabled?: boolean;
  status?: string;
  // Set only once the live-EHR site list is known (scraping EMRs).
  verification?: FacilityVerification;
};

export interface FormFieldOnChangeEvent {
  value: FormFieldValue;
  config: FormFieldConfig;
}

export interface FormOnChangeEvent {
  values: FormFieldValues;
  isValid: boolean;
  isDirty: boolean;
}

export enum FormFieldValidationType {
  Custom = 'custom',
  Regex = 'regex',
}

export interface FormFieldValidation {
  type: FormFieldValidationType;
  message: string;
}

export interface FormFieldCustomValidation extends FormFieldValidation {
  type: FormFieldValidationType.Custom;
  function: (value: FormFieldValue, config: FormFieldConfig, values: FormFieldValues) => boolean;
}

export interface FormFieldRegexValidation extends FormFieldValidation {
  type: FormFieldValidationType.Regex;
  regex: RegExp;
}

export type FormFieldValidations = FormFieldCustomValidation | FormFieldRegexValidation;

export enum FormFieldOperator {
  Or = 'or',
  And = 'and',
}

export interface FormFieldConditionGroup {
  operator: FormFieldOperator;
  conditions: (FormFieldCondition | FormFieldConditionGroup)[],
}

export interface FormFieldCondition {
  fieldKey: string;
  fieldValue: FormFieldValue;
}
